import { type DocumentData } from 'firebase/firestore';

import { type FromSchema } from 'json-schema-to-ts';

import {
  type AdviceType,
  CompanyTypesEnum,
  type CompanyTypesKeys,
  type MaritalStatus,
  MaritalStatusEnum,
  type SocialInsuranceFund,
  SocialInsuranceFundEnum,
  type SocialSecretariat,
  SocialSecretariatEnum,
  VatRegimeEnum,
  type VatRegimeKeys,
} from './enums';
import { type Address } from './general';
import { AddressSchema, type InvoiceCompanyData, type Template } from './invoiceTypes';
import { type OnboardingInfo } from './onboarding';

enum NthWeekdayInMonth {
  FIRST = 'first',
  SECOND = 'second',
  THIRD = 'third',
  FOURTH = 'fourth',
  LAST = 'last',
}

enum Weekday {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
}

type MonthOption = {
  value: string;
  label: string;
};

type NthWeekdayOfMonthOption = {
  value: NthWeekdayInMonth;
  label: string;
};

export const monthOptionsArray: MonthOption[] = [
  { value: 'january', label: 'Januari' },
  { value: 'february', label: 'Februari' },
  { value: 'march', label: 'Maart' },
  { value: 'april', label: 'April' },
  { value: 'may', label: 'Mei' },
  { value: 'june', label: 'Juni' },
  { value: 'july', label: 'Juli' },
  { value: 'august', label: 'Augustus' },
  { value: 'september', label: 'September' },
  { value: 'october', label: 'Oktober' },
  { value: 'november', label: 'November' },
  { value: 'december', label: 'December' },
];

export enum ExtraHolidayCondition {
  SAME_DAY = 'Zelfde dag',
  NEXT_WORK_DAY = 'Volgende werkdag',
  PREVIOUS_WORK_DAY = 'Vorige werkdag',
  NEXT_MONDAY = 'Volgende Maandag',
  NEXT_TUESDAY = 'Volgende Dinsdag',
  NEXT_WEDNESDAY = 'Volgende Woensdag',
  NEXT_THURSDAY = 'Volgende Donderdag',
  NEXT_FRIDAY = 'Volgende Vrijdag',
  NEXT_SATURDAY = 'Volgende Zaterdag',
  NEXT_SUNDAY = 'Volgende Zondag',
}

export const nthWeekdayOfMonthOptions: NthWeekdayOfMonthOption[] = [
  { value: NthWeekdayInMonth.FIRST, label: 'De eerste' },
  { value: NthWeekdayInMonth.SECOND, label: 'De tweede' },
  { value: NthWeekdayInMonth.THIRD, label: 'De derde' },
  { value: NthWeekdayInMonth.FOURTH, label: 'De vierde' },
  { value: NthWeekdayInMonth.LAST, label: 'De laatste' },
];

export const WeekdayOptions: { value: string; label: string }[] = [
  { value: Weekday.MONDAY, label: 'Maandag' },
  { value: Weekday.TUESDAY, label: 'Dinsdag' },
  { value: Weekday.WEDNESDAY, label: 'Woensdag' },
  { value: Weekday.THURSDAY, label: 'Donderdag' },
  { value: Weekday.FRIDAY, label: 'Vrijdag' },
  { value: Weekday.SATURDAY, label: 'Zaterdag' },
];

type PontoOngoingOnboardingInfo = {
  onboarding: {
    codeChallenge: string;
    codeVerifier: string;
  };
};

export type PontoFinishedOnboardingInfo = PontoOngoingOnboardingInfo & {
  accessToken: string;
  accessTokenExpiry: string;
  readyToAcceptWebhookData: boolean;
  refreshToken: string;
  refreshTokenExpiry: string;
};
export type PontoConfigurationInfo = PontoOngoingOnboardingInfo | PontoFinishedOnboardingInfo;

export type ConfigurationInfo = {
  clearfactsPurchaseEmail?: string;
  clearfactsSaleEmail?: string;
  clearfactsVariousEmail?: string;
  exactOnlinePurchaseEmail?: string;
  exactOnlineSaleEmail?: string;
  exactOnlineVariousEmail?: string;
  nova?: {
    activities: string;
    isConfiguredForNova?: boolean;
    excludedGlAccountCodes?: string[];
  };
  ponto?: PontoConfigurationInfo;
};

export type SubscriptionInfo = {
  monthlyPrice: number;
  adviceType: AdviceType;
  numberOfInvoicesIncluded: number;
  numberOfInvoicesUsed: number;
};

export enum PersonType {
  DIRECTOR = 'Bedrijfsleider',
  SHAREHOLDER = 'Aandeelhouder',
}

export type InvoiceInfo = {
  lastInvoiceNumber: number;
  hasDefaultTermsAndConditions?: boolean;
  defaultTermsAndConditions?: string;
  defaultInvoiceTemplateId?: Template;
  defaultHexColor?: string;
  defaultEmailBody?: string;
  defaultEmailSubject?: string;
  currentFinancialYearClosingDate?: Date;
  // NTH - defaultTemplate: string;
  // NTH - defaultInvoiceDescription: string;
  // NTH - defaultVatPercentage: number;
} & InvoiceCompanyData;

export type MyProfileDoc = {
  id: string | number;
} & DocumentData;

// AJV SCHEMAS
export const ConfigurationInfoBodySchema = {
  type: 'object',
  properties: {
    clearfactsPurchaseEmail: { type: 'string' },
    clearfactsSaleEmail: { type: 'string' },
    clearfactsVariousEmail: { type: 'string' },
    exactOnlinePurchaseEmail: { type: 'string' },
    exactOnlineSaleEmail: { type: 'string' },
    exactOnlineVariousEmail: { type: 'string' },
  },
  additionalProperties: false,
} as const;

// >>> START OF PERSONAL INFO SCHEMAS
export const PersonalInfoBodySchema = {
  type: 'object',
  properties: {
    firstName: { type: 'string' },
    lastName: { type: 'string' },
    maritalStatus: {
      type: 'string',
      enum: Object.keys(MaritalStatusEnum) as readonly MaritalStatus[],
    },
    nationalRegistryNumber: { type: 'string' },
    birthDay: { type: 'string' },
    address: AddressSchema,
    SSContributionType: { type: 'string' },
    iban: { type: 'string' },
    language: { type: 'string' },
    identityCard: {
      type: 'object',
      properties: {
        front: { type: 'string' },
        back: { type: 'string' },
        frontAndBack: { type: 'string' },
      },
      additionalProperties: false,
    },
    phone: { type: 'string' },
    hasHandicap: { type: ['boolean', 'integer'], enum: [0, 1, true, false] },
    hasDependants: { type: ['boolean', 'integer'], enum: [0, 1, true, false] },
    dependants: {
      type: 'object',
      properties: {
        amountOfChildren: { type: 'number' },
        amountOfChildrenWithHandicap: { type: 'number' },
        amountOfOthers: { type: 'number' },
        amountOfOthersWithHandicap: { type: 'number' },
        extraInfo: { type: 'string' },
      },
      additionalProperties: false,
    },
  },
  required: ['firstName', 'lastName'],
  additionalProperties: false,
} as const;

type PersonalInfoRequestBody = FromSchema<typeof PersonalInfoBodySchema>;

// Extending the UserPersonalInfo type with the additional properties
export type PersonalInfo = PersonalInfoRequestBody & {
  lastUpdatedAt?: Date;
};
// <<< END OF PERSONAL INFO SCHEMA

// >>> START OF COMPANY INFO SCHEMA
const directorSchema = {
  type: 'object',
  properties: {
    idFront: { type: 'string' },
    idBack: { type: 'string' },
    idFrontAndBack: { type: 'string' },
    isIdInOneFile: { type: 'boolean' },
    startMandate: { type: 'string', format: 'date-time' },
    endMandate: { type: 'string', format: 'date-time' },
    firstName: { type: 'string' },
    lastName: { type: 'string' },
    function: { type: 'string', enum: ['director'] },
    isDashboardUser: { type: 'boolean' },
    email: { type: 'string' },
    address: AddressSchema,
    nationalRegistryNumber: { type: 'string' },
  },
  // required: ["idFront", "idBack", "firstName", "lastName"],
  additionalProperties: false,
} as const;

export type Director = {
  idFront?: string;
  idBack?: string;
  idFrontAndBack?: string;
  isIdInOneFile?: boolean;
  startMandate?: Date;
  endMandate?: Date;
  firstName?: string;
  lastName?: string;
  function?: 'director';
  isDashboardUser?: boolean;
  email?: string;
  address?: Address;
  nationalRegistryNumber?: string;
};

export type IntakeFormDirector = Omit<Director, 'startMandate' | 'endMandate'> & {
  idFrontFile?: File | null;
  idBackFile?: File | null;
  idFrontAndBackFile?: File | null;
};

const shareholderSchema = {
  type: 'object',
  properties: {
    firstName: { type: 'string' },
    lastName: { type: 'string' },
    shareAmount: { type: 'number' },
    birthDay: { type: 'string', format: 'date-time' },
    ownershipRights: {
      type: 'string',
      enum: ['fullOwnership', 'bareOwnership', 'usefruct'],
    },
    voting: {
      type: 'object',
      properties: {
        hasRight: { type: 'boolean' },
        voteAmount: { type: 'number' },
      },
      required: ['hasRight', 'voteAmount'],
    },
    category: {
      type: 'string',
    },
    email: { type: 'string' },
    address: AddressSchema,
    nationalRegistryNumber: { type: 'string' },
  },
  required: ['firstName', 'lastName', 'shareAmount', 'voting'],
  additionalProperties: false,
} as const;

type Shareholder = {
  firstName?: string;
  lastName?: string;
  shareAmount?: number;
  birthDay?: Date;
  ownershipRights?: 'fullOwnership' | 'bareOwnership' | 'usefruct';
  voting?: {
    hasRight?: boolean;
    voteAmount?: number;
  };
  category?: string;
  email?: string;
  address?: Address;
  nationalRegistryNumber?: string;
};

const generalMeetingSchema = {
  type: 'object',
  properties: {
    date: { type: ['string', 'null'], format: 'date-time' },
    location: { type: 'string' },
    nthWeekdayInMonth: { type: 'string' },
    weekday: { type: 'string' },
    month: { type: 'string' },
    extraHolidayCondition: { type: 'string' },
    isSaturdayWorkday: { type: ['boolean', 'null'] },
  },
  additionalProperties: false,
} as const;

export const CompanyInfoBodySchema = {
  type: 'object',
  properties: {
    companyNumber: { type: 'string' },
    vatNumber: { type: 'string' },
    type: {
      type: 'string',
      enum: Object.keys(CompanyTypesEnum) as readonly CompanyTypesKeys[],
    },
    companyName: { type: 'string' },
    address: AddressSchema,
    socialInsuranceFund: {
      type: 'string',
      enum: Object.keys(SocialInsuranceFundEnum) as readonly SocialInsuranceFund[],
    },
    socialSecretariat: {
      type: 'string',
      enum: Object.keys(SocialSecretariatEnum) as readonly SocialSecretariat[],
    },
    language: { type: 'string' },
    vatRegimeType: {
      type: 'string',
      enum: Object.keys(VatRegimeEnum) as readonly VatRegimeKeys[],
    },
    hasProfessionalBankAccount: {
      type: ['boolean', 'integer'],
      enum: [0, 1, true, false],
    },
    hasProfessionalSavingsAccount: {
      type: ['boolean', 'integer'],
      enum: [0, 1, true, false],
    },
    professionalAccounts: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          iban: { type: 'string' },
          bic: { type: 'string' },
        },
        additionalProperties: false,
      },
    },
    articlesOfAssociation: { type: 'string' },
    articlesOfAssociationCompliance: {
      type: 'string',
      enum: ['yes', 'no', 'notApplicable'],
    },
    financialYearClosingDate: {
      type: 'string',
      pattern: '^\\d{2}\\/\\d{2}$',
    },
    amountOfShares: { type: 'number' },
    firstFinancialYearClosingDate: { type: 'string', format: 'date-time' },
    ubo: {
      type: 'object',
      properties: {
        hasUboRegister: {
          type: ['boolean', 'integer'],
          enum: [0, 1, true, false],
        },
        lastCheckedAt: { type: 'string', format: 'date-time' },
      },
      additionalProperties: false,
    },
    isOnlyDirector: {
      type: ['boolean', 'integer'],
      enum: [0, 1, true, false],
    },
    directors: {
      type: 'array',
      items: directorSchema,
    },
    shareholders: {
      type: 'array',
      items: shareholderSchema,
    },
    legalEntity: { type: 'string' },
    foundingData: {
      type: 'object',
      properties: {
        foundingDate: { type: 'string', format: 'date-time' },
        publicationDate: { type: 'string', format: 'date-time' },
      },
      additionalProperties: false,
    },
    generalMeeting: generalMeetingSchema,
    startDateRentalAgreement: { type: 'string', format: 'date-time' },
    activities: { type: 'string' },
  },
  additionalProperties: false,
} as const;

type FleetFiscalType = 'rollendMaterieel' | 'personenwagen';

type FleetItem = {
  description?: string;
  licensePlate?: string;
  fiscalType: FleetFiscalType;
};

export type Fleet = FleetItem[];

export type CompanyInfo = {
  companyNumber?: string;
  vatNumber?: string;
  vatRegimeType?: VatRegimeKeys;
  type?: CompanyTypesKeys;
  companyName?: string;
  address?: Address;
  socialInsuranceFund?: SocialInsuranceFund;
  socialSecretariat?: SocialSecretariat;
  hasProfessionalBankAccount?: boolean;
  professionalAccounts?: { iban: string }[];
  articlesOfAssociation?: string;
  articlesOfAssociationCompliance?: 'yes' | 'no' | 'notApplicable';
  financialYearClosingDate?: string;
  amountOfShares?: number;
  language?: string;
  firstFinancialYearClosingDate?: Date;
  ubo?: {
    hasUboRegister?: boolean;
    lastCheckedAt?: Date;
  };
  isOnlyDirector?: boolean;
  directors?: Director[];
  shareholders?: Shareholder[];
  legalEntity?: string;
  foundingData?: {
    foundingDate?: Date;
    publicationDate?: Date;
  };
  generalMeeting?: {
    date?: Date;
    location?: string;
    nthWeekdayInMonth?: string;
    weekday?: string;
    month?: string;
    extraHolidayCondition?: ExtraHolidayCondition;
    isSaturdayWorkday?: boolean;
  };
  startDateRentalAgreement?: Date;
  fleet?: Fleet;
};

export const SubscriptionInfoBodySchema = {
  type: 'object',
  properties: {
    monthlyPrice: { type: 'number' },
    adviceType: {
      type: 'string',
      enum: ['basic', 'extensive', 'basis', 'uitgebreid'],
    },
    numberOfInvoicesIncluded: { type: 'number' },
    numberOfInvoicesUsed: { type: 'number' },
  },
  required: ['monthlyPrice'],
  additionalProperties: false,
} as const;

export const InvoiceInfoBodySchema = {
  type: 'object',
  properties: {
    logo: { type: 'string' },
    address: AddressSchema,
    bic: { type: 'string' },
    iban: { type: 'string' },
    lastInvoiceNumber: { type: 'number' },
    name: { type: 'string' },
    vatNumber: { type: 'string' },
  },
  additionalProperties: false,
} as const;

/**
 * Used for the user profile page from FiscalProfileContext
 *  */
export type BasicUserSubscriptionInfo = {
  subscriptionInfo: SubscriptionInfo | null;
  companyInfo: CompanyInfo | null;
};

export type UserInfo = {
  companyInfo: CompanyInfo;
  configurationInfo: ConfigurationInfo;
  invoiceInfo: InvoiceInfo;
  subscriptionInfo: SubscriptionInfo;
  onboardingInfo: OnboardingInfo;
  personalInfo: PersonalInfo;
};
